import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Dimensions, Keyboard, View } from 'react-native';
import PropTypes from 'prop-types';
import { Device } from 'DeviceUtils';
import Item from './Item';
import Button from './Button';
import {
    getPosition,
    getLabelValueMap,
    getDefaultLabel,
    createButtonComponent,
    getTransformedChildren,
    conditionallyWrapComponent
} from './utils';
import DropDownMenuWrapper from './DropdownMenuWrapper';
import ModalWrapper from './ModalWrapper';
import PickerMenuContent from './PickerMenuContent';
import DropdownContent from './DropdownContent';

const Picker = (props) => {
    const {
        selectedValue,
        children,
        backgroundColor,
        menuContainerStyle,
        withArrow,
        data,
        mode,
        hasTVPreferredFocus,
        onOpen,
        onClose,
        portal,
        isDisplayedInDetailsPopUp
    } = props;

    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    const [iosKeyboardDisplayed, setIosKeyboardDisplayed] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ horizontal: 'left', vertical: 'top' });
    const pickerRef = useRef();

    // Modal handling for IOS
    // Do not allow to dislay modal until after keyboard is hidden
    // There is no await in keyboard.dismiss() so manual timeout is required
    const checkAndSetDropDownOpen = (open) => {
        if (!Device.isIos || !open) setDropdownOpen(open);
        if (iosKeyboardDisplayed) {
            Keyboard.dismiss();
            setTimeout(() => {
                setDropdownOpen(open);
            }, 300);
        } else {
            setDropdownOpen(open);
        }
    };

    const onLayout = () => {
        const { height, width } = Dimensions.get('window');

        pickerRef.current.measure((x, y, buttonWidth, buttonHeight, pageX, pageY) => {
            const position = getPosition(
                {
                    x,
                    y,
                    height,
                    width,
                    pageX,
                    pageY,
                    buttonWidth,
                    buttonHeight
                },
                portal,
                isDisplayedInDetailsPopUp
            );
            setMenuPosition(position);
        });
    };

    useLayoutEffect(() => {
        onLayout();
    }, [isDropdownOpen]);

    // Modal handling for IOS
    // On IOS there are problems with modals interfering with keyboard and alerts
    // This effect prevents accidental freezing of the app; which "thinks" that
    // a modal is opened when no modal is actually displayed - essentially locking the UI
    useEffect(() => {
        if (!Device.isIos) return () => {};

        // When keyboard is displayed hide the modal
        // and "disallow" reopening modal until the keyboard is hidden
        const showSubscription = Keyboard.addListener('keyboardWillShow', () => {
            setIosKeyboardDisplayed(true);
            setDropdownOpen(false);
        });
        // When keyboard is hidden allow to dislay modal
        const hideSubscription = Keyboard.addListener('keyboardDidHide', () => {
            setIosKeyboardDisplayed(false);
        });

        return () => {
            showSubscription.remove();
            hideSubscription.remove();
        };
    }, []);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        if (!isMounted) return;
        if (isDropdownOpen) {
            onOpen();
        } else {
            onClose();
        }
    }, [isDropdownOpen]);

    const dropDownOnLayout = (event) => {
        setMenuPosition({ ...menuPosition, dropDownDimensions: event.nativeEvent.layout });
    };

    const childrenArray = React.Children.toArray(children);

    const labelValueMap = getLabelValueMap(data, childrenArray);

    const selectedLabel = labelValueMap[selectedValue] || getDefaultLabel(props);
    const ButtonComponent = createButtonComponent(
        childrenArray,
        selectedLabel,
        checkAndSetDropDownOpen,
        hasTVPreferredFocus
    );

    const transformedChildren = getTransformedChildren(
        props,
        childrenArray,
        checkAndSetDropDownOpen
    );

    const WrappedComponent = conditionallyWrapComponent(
        () => <PickerMenuContent transformedChildren={transformedChildren} />,
        mode === 'dialog',
        ({ children: innerChildren }) => (
            <ModalWrapper {...{ children: innerChildren, menuContainerStyle }} />
        ),
        ({ children: innerChildren }) => (
            <DropDownMenuWrapper
                {...{
                    backgroundColor,
                    menuContainerStyle,
                    menuPosition,
                    withArrow,
                    portal,
                    children: innerChildren,
                    onLayout: dropDownOnLayout,
                    closeDropDown: () => {
                        setDropdownOpen(false);
                    }
                }}
            />
        )
    );

    return (
        <View onLayout={onLayout} ref={pickerRef} style={{ flex: 1 }}>
            {ButtonComponent}
            {isDropdownOpen ? WrappedComponent : null}
        </View>
    );
};

Picker.Item = Item;

Picker.Button = Button;

Picker.DropdownContent = DropdownContent;

Picker.defaultProps = {
    selectedValue: '',
    children: null,
    backgroundColor: '#1260d7',
    menuContainerStyle: {},
    withArrow: false,
    data: null,
    mode: 'dialog',
    hasTVPreferredFocus: false,
    onOpen: () => {},
    onClose: () => {},
    portal: false
};

Picker.propTypes = {
    selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    backgroundColor: PropTypes.string,
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    menuContainerStyle: PropTypes.oneOfType([PropTypes.arrayOf({}), PropTypes.shape({})]),
    withArrow: PropTypes.bool,
    data: PropTypes.arrayOf(PropTypes.shape({})),
    mode: PropTypes.oneOf(['dropdown', 'dialog']),
    hasTVPreferredFocus: PropTypes.bool,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    portal: PropTypes.bool
};

export default Picker;
